import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';

/**
 * Who is signed in, according to the SERVER (§0.5 "the server is the source of
 * truth"). The UI never decides for itself that someone is signed in, and never
 * decides for itself what they may do — it asks and obeys.
 */

export interface Me {
  staff: { id: string; name: string; role: 'admin' | 'sales' };
  device: 'phone' | 'cart';
  cart: { id: string; name: string | null } | null;
  /** Phones cannot start calls (§3). The server states this; the UI renders accordingly. */
  can_start_call: boolean;
}

type State =
  | { status: 'loading' }
  | { status: 'anon' }
  | { status: 'in'; me: Me }
  | { status: 'error'; message: string };

interface Ctx {
  state: State;
  refresh: (opts?: { userActive?: boolean }) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Ctx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  const refresh = useCallback(async (opts?: { userActive?: boolean }) => {
    try {
      // A public probe: "nobody is signed in" is a normal 200 answer, not an
      // error, so an anonymous visitor produces no failed request in the console.
      const res = await api<{ signed_in: boolean } & Partial<Me>>('/api/auth/me', {
        userActive: opts?.userActive ?? false,
      });

      if (!res.signed_in || !res.staff) {
        setState({ status: 'anon' });
        return;
      }

      setState({
        status: 'in',
        me: {
          staff: res.staff,
          device: res.device ?? 'phone',
          cart: res.cart ?? null,
          can_start_call: res.can_start_call ?? false,
        },
      });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof ApiError ? err.message : 'Could not reach the shop system.',
      });
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      // Whatever the server said, this browser is done. Server-side revocation
      // already happened inside the request above.
      setState({ status: 'anon' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // No stale screens (§0.5): re-check on tab focus.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return <SessionContext.Provider value={{ state, refresh, signOut }}>{children}</SessionContext.Provider>;
}

export function useSession(): Ctx {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
